/**
 * Helperi PURI pentru „Sign in with Google" (sub-planul 06).
 *
 * `authApi.exchange` / hook-ul / butonul de UI nu exista inca — endpointul
 * `POST /api/auth/exchange` e in lucru in alt worktree. Astea doua functii nu au nevoie de el:
 * decid doar daca butonul are ce randa si ce mesaj arata daca exchange-ul viitor esueaza.
 */

import { jwtDecode } from 'jwt-decode';

import { getErrorMessage, getErrorStatus } from '../../../core/api/response-handler';

/**
 * `VITE_GOOGLE_CLIENT_ID` nu exista inca in `.env`/`.env.example` (task separat) — citita ca
 * functie, nu ca gate la nivel de modul (spre deosebire de `app-insights.ts`), pentru ca
 * `GoogleSignInButton` (task viitor) trebuie sa decida la fiecare randare daca se afiseaza.
 */
export function isGoogleSignInAvailable(): boolean {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  return typeof clientId === 'string' && clientId.trim().length > 0;
}

/**
 * 401 acopera trei cazuri distincte in backend (fara cont local, cont dezactivat, token extern
 * invalid) — backendul NU le distinge intentionat catre apelant, deci mesajul ramane generic,
 * fara sa afirme o cauza anume.
 */
export function exchangeErrorMessage(error: unknown): string {
  const status = getErrorStatus(error);

  if (status === 401) {
    return 'Nu am gasit un cont activ cu acest email. Daca esti profesor sau elev nou, inscrie-te mai intai.';
  }

  if (status === 503) {
    return 'Autentificarea cu Google e temporar indisponibila. Incearca din nou peste cateva minute.';
  }

  return getErrorMessage(error, 'Autentificarea cu Google a esuat. Incearca din nou.');
}

/**
 * Acelasi tipar ca useLoginWithForm: 401 acopera si "cont in editia trecuta" si "cont
 * inexistent" - backendul nu le distinge intentionat catre apelant, deci NU incercam sa
 * ghicim cauza, doar aratam linkul de reinscriere.
 */
export function shouldShowReenrollHint(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

/**
 * Emailul din credentialul Google (JWT-ul emis de GIS, are claim-ul `email`) - best-effort,
 * pentru pre-completarea formularului de reinscriere. `undefined` la orice esec de decodare,
 * niciodata aruncat: userul completeaza manual daca nu merge.
 */
export function extractGoogleCredentialEmail(credential: string): string | undefined {
  try {
    return jwtDecode<{ email?: string }>(credential).email;
  } catch {
    return undefined;
  }
}
